import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";

/**
 * Le regole della password, spuntate mentre si scrive.
 *
 * Prima le regole non erano scritte da nessuna parte: si scopriva di averle
 * infrante solo dopo aver premuto «Registrati», e per giunta da un messaggio
 * illeggibile. Mostrarle prima significa che chi si registra non sbaglia,
 * invece di sbagliare e dover indovinare cosa correggere.
 *
 * Le regole qui riprodotte sono **le stesse del server**
 * (`_PASSWORD_RE` in `app/api/v1/auth.py`): se lì cambiano, qui vanno cambiate,
 * altrimenti la spunta verde promette qualcosa che poi viene rifiutato.
 */

export const REGOLE_PASSWORD = [
  { chiave: "lunghezza", verifica: (p: string) => p.length >= 8 },
  { chiave: "maiuscola", verifica: (p: string) => /[A-Z]/.test(p) },
  { chiave: "minuscola", verifica: (p: string) => /[a-z]/.test(p) },
  { chiave: "cifra", verifica: (p: string) => /\d/.test(p) },
] as const;

/** Un requisito in più, consigliato ma non preteso dal server. */
const SIMBOLO = {
  chiave: "simbolo",
  verifica: (p: string) => /[^A-Za-z0-9]/.test(p),
};

export function passwordValida(password: string): boolean {
  return REGOLE_PASSWORD.every((r) => r.verifica(password));
}

export function RequisitiPassword({
  password,
  mostraSempre = false,
}: {
  password: string;
  /** Se falso, l'elenco compare solo quando si inizia a scrivere. */
  mostraSempre?: boolean;
}) {
  const { t } = useTranslation();
  const cfg = useSiteConfig();

  if (!mostraSempre && !password) return null;

  const voci = [...REGOLE_PASSWORD, SIMBOLO].map((r) => ({
    chiave: r.chiave,
    soddisfatto: r.verifica(password),
    obbligatorio: r.chiave !== "simbolo",
  }));

  return (
    <div
      // `polite` e non `assertive`: l'elenco si aggiorna a ogni tasto, e un
      // annuncio insistente renderebbe il campo inutilizzabile con un lettore
      // di schermo.
      aria-live="polite"
      style={{
        marginTop: 10,
        padding: "12px 14px",
        background: cfg.color_light_bg,
        borderRadius: 8,
        fontSize: 12.5,
        lineHeight: 1.6,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: cfg.color_muted,
          marginBottom: 6,
          fontWeight: 600,
        }}
      >
        {t("auth.passwordRules.title")}
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {voci.map((v) => (
          <li
            key={v.chiave}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              color: v.soddisfatto ? cfg.color_primary : cfg.color_dark,
            }}
          >
            <span aria-hidden="true" style={{ width: 14, flexShrink: 0, fontWeight: 700 }}>
              {v.soddisfatto ? "✓" : "·"}
            </span>
            <span>
              {t(`auth.passwordRules.${v.chiave}`)}
              {!v.obbligatorio && (
                <span style={{ color: cfg.color_muted }}>
                  {" "}
                  {t("auth.passwordRules.consigliato")}
                </span>
              )}
              {/* Il colore da solo non basta: chi non distingue verde e nero
                  vedrebbe cinque righe identiche. Il testo dice lo stato. */}
              <span
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                }}
              >
                {" "}
                {v.soddisfatto
                  ? t("auth.passwordRules.soddisfatto")
                  : t("auth.passwordRules.mancante")}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
